import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ZONES, zoneLabel } from "@/lib/zones";
import { useLang } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { InjuryTimeline } from "@/components/InjuryTimeline";
import { PainAlerts } from "@/components/PainAlerts";
import { toast } from "sonner";
import { Trash2, HeartPulse, ShieldAlert, Activity } from "lucide-react";

const L = {
  en: { title: "Medical Body Map", subtitle: "Tap a body part to log an injury or a weak spot. Get an AI-generated prevention protocol per zone.",
    add: "Log entry", zone: "Zone", kindInjury: "Injury", kindWeakness: "Weakness",
    label: "Type", labelPh: "e.g. Osgood-Schlatter, Tight hamstring, Ankle sprain",
    severity: "Severity", notes: "Notes", save: "Save & analyze", saving: "Analyzing…",
    yourEntries: "Your medical profile", protocol: "Prevention protocol", exercises: "Recommended exercises",
    warning: "Warning signs", seeDoc: "See a doctor if…", noSelect: "Select a zone on the body",
    fromDaily: "Pain flagged in your Daily Log — log the injury here to get a protocol.",
    checkin: "Update", checkinNote: "How does it feel today?", checkinSave: "Save check-in", healed: "Healed", now: "now", history: "Evolution" },
  fr: { title: "Carte Médicale", subtitle: "Clique sur une zone du corps pour enregistrer une blessure ou un point faible. Reçois un protocole de prévention IA.",
    add: "Nouvelle entrée", zone: "Zone", kindInjury: "Blessure", kindWeakness: "Faiblesse",
    label: "Type", labelPh: "ex : Osgood-Schlatter, ischios tendus, entorse cheville",
    severity: "Sévérité", notes: "Notes", save: "Enregistrer & analyser", saving: "Analyse…",
    yourEntries: "Ton profil médical", protocol: "Protocole de prévention", exercises: "Exercices recommandés",
    warning: "Signes d'alerte", seeDoc: "Consulte un médecin si…", noSelect: "Sélectionne une zone sur le corps",
    fromDaily: "Douleur signalée dans ton Suivi Quotidien — enregistre la blessure ici pour obtenir un protocole.",
    checkin: "Mettre à jour", checkinNote: "Comment ça va aujourd'hui ?", checkinSave: "Enregistrer le suivi", healed: "Guéri", now: "actuel", history: "Évolution" },
};

function BodyMap({ items, selected, onSelect }) {
  const counts = {};
  items.forEach((it) => { if ((it.current_severity ?? it.severity) > 0) counts[it.zone] = (counts[it.zone] || 0) + 1; });
  return (
    <svg viewBox="0 0 300 600" className="w-full max-w-xs mx-auto" data-testid="medical-body-svg">
      <g fill="#121721" stroke="#1E2638" strokeWidth="1.5">
        <ellipse cx="150" cy="45" rx="30" ry="34" />
        <rect x="140" y="78" width="20" height="18" rx="4" />
        <path d="M90 110 Q150 100 210 110 L215 220 Q200 240 180 240 L120 240 Q100 240 85 220 Z" />
        <path d="M100 230 L115 400 L130 500 L120 570 L135 575 L140 500 L150 400 L160 500 L165 575 L180 570 L170 500 L185 400 L200 230 Z" />
        <ellipse cx="85" cy="150" rx="12" ry="45" transform="rotate(-8 85 150)" />
        <ellipse cx="215" cy="150" rx="12" ry="45" transform="rotate(8 215 150)" />
      </g>
      {ZONES.map((z) => {
        const c = counts[z.id] || 0;
        const isSel = selected === z.id;
        return (
          <g key={z.id} onClick={() => onSelect(z.id)} className="cursor-pointer" data-testid={`zone-${z.id}`}>
            <circle cx={z.x} cy={z.y} r={z.r}
              fill={c > 0 ? "rgba(255, 46, 84, 0.35)" : isSel ? "rgba(0,240,118,0.35)" : "rgba(0,240,118,0.06)"}
              stroke={c > 0 ? "#FF2E54" : isSel ? "#00F076" : "#1E2638"}
              strokeWidth={isSel ? 2 : 1} className="transition-all hover:fill-emerald-500/25" />
            {c > 0 && <text x={z.x} y={z.y + 4} textAnchor="middle" fill="#fff" fontSize="10" fontWeight="700">{c}</text>}
          </g>
        );
      })}
    </svg>
  );
}

function CheckinForm({ item, t, onDone }) {
  const [sev, setSev] = useState(item.current_severity ?? item.severity ?? 2);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/medical/${item.id}/checkin`, { severity: parseInt(sev), note }); onDone(); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 rounded-md border border-[#1E2638] bg-[#121721] p-2 space-y-2" data-testid={`checkin-form-${item.id}`}>
      <Label className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-400 flex justify-between">
        <span>{t.severity}</span><span className={sev === 0 ? "text-[#00F076]" : "text-amber-400"}>{sev === 0 ? t.healed : `${sev}/5`}</span>
      </Label>
      <input type="range" min="0" max="5" value={sev} onChange={(e) => setSev(parseInt(e.target.value))} data-testid={`checkin-severity-${item.id}`} className="w-full accent-[#00F076]" />
      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t.checkinNote} data-testid={`checkin-note-${item.id}`} className="bg-[#090C10] border-[#1E2638] text-white h-8 text-xs" />
      <Button size="sm" onClick={submit} disabled={busy} data-testid={`checkin-save-${item.id}`} className="w-full h-7 bg-[#00F076] hover:bg-[#00C963] text-black text-xs font-bold uppercase">{t.checkinSave}</Button>
    </div>
  );
}

export default function Medical() {
  const { lang } = useLang();
  const t = L[lang] || L.en;
  const [params, setParams] = useSearchParams();
  const fromDaily = params.get("zone");
  const [items, setItems] = useState([]);
  const [timeline, setTimeline] = useState(null);
  const [selected, setSelected] = useState(fromDaily || null);
  const [form, setForm] = useState({ kind: "injury", label: "", severity: 2, notes: "" });
  const [saving, setSaving] = useState(false);
  const [openCheckin, setOpenCheckin] = useState(null);

  const load = () => Promise.all([
    api.get("/medical").then((r) => setItems(r.data)),
    api.get("/medical/timeline?weeks=8").then((r) => setTimeline(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!selected) return toast.error(t.noSelect);
    if (!form.label.trim()) return;
    setSaving(true);
    try {
      await api.post("/medical", { zone: selected, ...form, severity: parseInt(form.severity) });
      toast.success("Saved");
      setForm({ kind: "injury", label: "", severity: 2, notes: "" });
      if (fromDaily) setParams({});
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const del = async (id) => { await api.delete(`/medical/${id}`); load(); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="medical-page">
      <div className="text-[10px] font-mono-tech tracking-[0.3em] text-emerald-400">SPORTS MEDICINE</div>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl mt-2" data-testid="medical-title">{t.title}</h1>
      <p className="text-slate-400 mt-2 max-w-2xl">{t.subtitle}</p>

      <div className="mt-6"><PainAlerts compact /></div>

      {fromDaily && (
        <div className="mt-4 rounded-lg border border-amber-400/40 bg-amber-400/10 px-4 py-3 text-sm text-amber-200 flex items-center gap-2" data-testid="medical-from-daily-banner">
          <Activity className="w-4 h-4 shrink-0" />
          <span><b>{zoneLabel(fromDaily, lang)}</b> — {t.fromDaily}</span>
        </div>
      )}

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-6">
          <BodyMap items={items} selected={selected} onSelect={setSelected} />
          <div className="mt-4 text-center text-xs font-mono-tech uppercase tracking-wider text-slate-400" data-testid="medical-selected-zone">
            {selected ? <>Selected: <span className="text-[#00F076]">{zoneLabel(selected, lang)}</span></> : t.noSelect}
          </div>
        </div>

        <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-6 space-y-3">
          <div className="font-display font-bold uppercase text-lg">{t.add}</div>
          <div>
            <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">Type</Label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {[["injury", t.kindInjury, ShieldAlert], ["weakness", t.kindWeakness, HeartPulse]].map(([id, lbl, Icon]) => (
                <button key={id} onClick={() => setForm({ ...form, kind: id })} data-testid={`medical-kind-${id}`}
                  className={`px-3 py-2 rounded-md text-xs font-semibold uppercase tracking-wide border transition-all flex items-center justify-center gap-1 ${
                    form.kind === id ? "bg-[#00F076] text-black border-[#00F076]" : "bg-[#090C10] border-[#1E2638] text-slate-300"}`}
                ><Icon className="w-3 h-3" />{lbl}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{t.label}</Label>
            <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder={t.labelPh} data-testid="medical-label-input" className="mt-1 bg-[#090C10] border-[#1E2638] text-white" />
          </div>
          <div>
            <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{t.severity} · {form.severity}/5</Label>
            <input type="range" min="1" max="5" value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} data-testid="medical-severity-slider" className="w-full mt-2 accent-[#00F076]" />
          </div>
          <div>
            <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{t.notes}</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="medical-notes-input" className="mt-1 bg-[#090C10] border-[#1E2638] text-white" />
          </div>
          <Button onClick={save} disabled={saving || !selected || !form.label.trim()} data-testid="medical-save-btn" className="w-full bg-[#00F076] hover:bg-[#00C963] text-black font-bold uppercase tracking-wider">
            {saving ? t.saving : t.save}
          </Button>
        </div>

        <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-6 space-y-3 max-h-[720px] overflow-auto">
          <div className="font-display font-bold uppercase text-lg">{t.yourEntries}</div>
          {items.length === 0 && <div className="text-slate-500 text-sm">—</div>}
          {items.map((m) => {
            const cur = m.current_severity ?? m.severity;
            return (
            <div key={m.id} className="rounded-lg border border-[#1E2638] bg-[#090C10] p-3" data-testid={`medical-item-${m.id}`}>
              <div className="flex justify-between items-start">
                <div className="min-w-0">
                  <div className="text-[10px] font-mono-tech tracking-wider uppercase text-slate-400">
                    {zoneLabel(m.zone, lang)} · {m.kind} · <span className={cur === 0 ? "text-[#00F076]" : "text-amber-400"}>{cur === 0 ? t.healed : `sev ${cur}/5`}</span>
                  </div>
                  <div className="font-display font-bold text-base mt-0.5">{m.label}</div>
                </div>
                <div className="flex shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => setOpenCheckin(openCheckin === m.id ? null : m.id)} className="text-slate-400 hover:text-[#00F076] h-7 px-2 text-xs" data-testid={`medical-checkin-toggle-${m.id}`}>
                    {t.checkin}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => del(m.id)} className="text-slate-400 hover:text-red-400 h-7 px-2" data-testid={`medical-delete-${m.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              {openCheckin === m.id && <CheckinForm item={m} t={t} onDone={() => { setOpenCheckin(null); load(); }} />}
              {m.checkins?.length > 0 && (
                <div className="mt-2 flex items-center gap-1 flex-wrap" data-testid={`medical-checkins-${m.id}`}>
                  <span className="text-[10px] font-mono-tech uppercase text-slate-500 mr-1">{t.history}</span>
                  <span className="text-[10px] font-mono-tech px-1.5 py-0.5 rounded bg-[#1E2638] text-slate-300">{m.severity}</span>
                  {m.checkins.map((c) => (
                    <span key={c.id} title={`${new Date(c.created_at).toLocaleDateString()} ${c.note || ""}`}
                      className={`text-[10px] font-mono-tech px-1.5 py-0.5 rounded ${c.severity === 0 ? "bg-[#00F076]/20 text-[#00F076]" : "bg-[#1E2638] text-slate-300"}`}>→ {c.severity}</span>
                  ))}
                </div>
              )}
              {m.ai?.overview && <div className="mt-2 text-xs text-slate-300 leading-relaxed">{m.ai.overview}</div>}
              {m.ai?.prevention_protocol?.length > 0 && (
                <div className="mt-2">
                  <div className="text-[10px] font-mono-tech uppercase text-[#00F076] mb-1">{t.protocol}</div>
                  <ul className="list-disc list-inside space-y-0.5 text-xs text-slate-300">{m.ai.prevention_protocol.map((s, i) => <li key={i}>{s}</li>)}</ul>
                </div>
              )}
              {m.ai?.exercises?.length > 0 && (
                <div className="mt-2">
                  <div className="text-[10px] font-mono-tech uppercase text-amber-400 mb-1">{t.exercises}</div>
                  {m.ai.exercises.map((ex, i) => (
                    <div key={i} className="text-xs text-slate-300">
                      <span className="text-white font-semibold">{ex.name}</span>
                      {ex.frequency && <span className="text-[#00F076]"> · {ex.frequency}</span>}
                      {ex.how && <div className="text-slate-400 pl-3">{ex.how}</div>}
                    </div>
                  ))}
                </div>
              )}
              {m.ai?.warning_signs?.length > 0 && (
                <div className="mt-2">
                  <div className="text-[10px] font-mono-tech uppercase text-red-400 mb-1">{t.warning}</div>
                  <ul className="list-disc list-inside space-y-0.5 text-xs text-slate-300">{m.ai.warning_signs.map((s, i) => <li key={i}>{s}</li>)}</ul>
                </div>
              )}
              {m.ai?.when_to_see_doctor && (
                <div className="mt-2 text-xs text-slate-400 italic border-l-2 border-red-500/50 pl-2">{t.seeDoc} {m.ai.when_to_see_doctor}</div>
              )}
            </div>
          );})}
        </div>
      </div>

      <div className="mt-6">
        <InjuryTimeline data={timeline} lang={lang} selectedZone={selected} onSelectZone={setSelected} />
      </div>
    </div>
  );
}
